import logger from "@/lib/logger";
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  CircuitBoard, Plus, Search, Eye, Send, Loader2, X, Package, Layers, PackageOpen,
  Undo2, Wrench, BadgeCheck, Upload, ImageIcon, Camera, Link2, ExternalLink, Trash2,
  Minus, History, ClipboardList, Copy, ArrowRight, AlertTriangle, CheckCircle2, User, Phone, MapPin
} from "lucide-react";
import {
  PCB_STAGES, STOCK_STATUS_STYLES, CONNECTOR_TYPES, todayDDMMYYYY, isValidDDMMYYYY, maskDDMMYYYY, pcbTrackUrl, prettyDate
} from "@/lib/pcb";

const FONT = { fontFamily: "'Space Grotesk',sans-serif" };
const fmtDT = d => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const EMPTY_MODEL = { model: "", pcb_code: "", voltage_current: "", connector_type: "", stock: "", low_stock_threshold: "10", notes: "" };
const EMPTY_SEND  = { job_ref: "", customer_name: "", customer_phone: "", customer_address: "", charger_model: "", charger_serial: "", charger_photo_url: "", courier_name: "", tracking_id: "", dispatch_date: todayDDMMYYYY(), notes: "" };

/** Small 6-dot stage progress used in the dispatch table */
export const StageDots = ({ idx }) => (
  <div className="flex items-center gap-1" title={idx >= 0 ? PCB_STAGES[idx].label : "Not started"}>
    {PCB_STAGES.map((s, i) => (
      <span key={s.key} className={`h-2 w-2 rounded-full ${i <= idx ? s.bg : "bg-slate-200"}`} />
    ))}
  </div>
);

export default function PCBInventory() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "dispatches" ? "dispatches" : "inventory";
  const setTab = (t) => setParams(t === "inventory" ? {} : { tab: t });

  // ── Inventory ──
  const [models, setModels]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [stats, setStats]       = useState(null);

  // ── Dispatches ──
  const [dispatches, setDispatches] = useState([]);
  const [loadingD, setLoadingD]     = useState(false);
  const [dSearch, setDSearch]       = useState("");
  const [dFilter, setDFilter]       = useState("all");

  // ── Add Stock (new model) ──
  const [showAdd, setShowAdd]   = useState(false);
  const [addForm, setAddForm]   = useState(EMPTY_MODEL);
  const [saving, setSaving]     = useState(false);

  // ── View sheet ──
  const [viewId, setViewId]     = useState(null);
  const [viewData, setViewData] = useState(null);
  const [loadingV, setLoadingV] = useState(false);
  const [adjQty, setAdjQty]     = useState("1");
  const [adjusting, setAdjusting] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showDel, setShowDel]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Send dialog ──
  const [sendPcb, setSendPcb]   = useState(null);
  const [sendForm, setSendForm] = useState(EMPTY_SEND);
  const [sending, setSending]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupInfo, setLookupInfo] = useState(null);
  const fileRef = useRef(null);

  // ─── Fetchers ──────────────────────────────────
  const fetchStats = useCallback(async () => {
    try { const r = await api.get("/pcb/stats"); setStats(r.data); }
    catch (err) { logger.error("[PCB] stats", err?.message); }
  }, []);

  const fetchModels = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await api.get("/pcb/models", { params: q ? { search: q } : {} });
      setModels(r.data.items || []);
    } catch (err) { logger.error("[PCB] models", err?.message); toast.error("Failed to load PCB inventory"); }
    finally { setLoading(false); }
  }, []);

  const fetchDispatches = useCallback(async (q, f) => {
    setLoadingD(true);
    try {
      const p = {}; if (q) p.search = q; if (f && f !== "all") p.stage = f;
      const r = await api.get("/pcb/dispatches", { params: p });
      setDispatches(r.data.items || []);
    } catch (err) { logger.error("[PCB] dispatches", err?.message); toast.error("Failed to load dispatch records"); }
    finally { setLoadingD(false); }
  }, []);

  // Live search (debounced 250ms)
  useEffect(() => { const t = setTimeout(() => fetchModels(search.trim()), 250); return () => clearTimeout(t); }, [search, fetchModels]);
  useEffect(() => { if (tab === "dispatches") { const t = setTimeout(() => fetchDispatches(dSearch.trim(), dFilter), 250); return () => clearTimeout(t); } }, [tab, dSearch, dFilter, fetchDispatches]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const refreshAll = () => { fetchModels(search.trim()); fetchStats(); if (tab === "dispatches") fetchDispatches(dSearch.trim(), dFilter); };

  // ─── Add model ────────────────────────────────
  const canAdd = addForm.model.trim() && addForm.pcb_code.trim() && addForm.stock !== "" && Number(addForm.stock) >= 0;
  const submitAdd = async () => {
    if (!canAdd) return;
    setSaving(true);
    try {
      await api.post("/pcb/models", { ...addForm, stock: parseInt(addForm.stock) || 0, low_stock_threshold: parseInt(addForm.low_stock_threshold) || 10 });
      toast.success(`PCB model ${addForm.pcb_code} added with ${addForm.stock} units`);
      setShowAdd(false); setAddForm(EMPTY_MODEL); refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add PCB model"); }
    finally { setSaving(false); }
  };

  // ─── View sheet ───────────────────────────────
  const openView = async (id) => {
    setViewId(id); setViewData(null); setEditForm(null); setLoadingV(true);
    try { const r = await api.get(`/pcb/models/${id}`); setViewData(r.data); }
    catch { toast.error("Failed to load PCB details"); setViewId(null); }
    finally { setLoadingV(false); }
  };
  const adjustStock = async (sign) => {
    const q = (parseInt(adjQty) || 0) * sign;
    if (!q) return;
    setAdjusting(true);
    try {
      const r = await api.patch(`/pcb/models/${viewId}/stock`, { quantity: q, reason: q > 0 ? "Stock added" : "Stock removed" });
      setViewData(v => ({ ...v, ...r.data }));
      toast.success(q > 0 ? `Added ${q} unit${q !== 1 ? "s" : ""}` : `Removed ${Math.abs(q)} unit${q !== -1 ? "s" : ""}`);
      refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Stock update failed"); }
    finally { setAdjusting(false); }
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const r = await api.put(`/pcb/models/${viewId}`, { ...editForm, low_stock_threshold: parseInt(editForm.low_stock_threshold) || 10 });
      setViewData(v => ({ ...v, ...r.data })); setEditForm(null); toast.success("PCB model updated"); refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setSavingEdit(false); }
  };
  const deleteModel = async () => {
    setDeleting(true);
    try {
      await api.delete(`/pcb/models/${viewId}`); toast.success("PCB model deleted");
      setShowDel(false); setViewId(null); refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };

  // ─── Send dialog ──────────────────────────────
  const openSend = (pcb) => {
    if (pcb.stock <= 0) { toast.error(`${pcb.pcb_code} is out of stock`); return; }
    setSendPcb(pcb); setSendForm({ ...EMPTY_SEND, dispatch_date: todayDDMMYYYY() }); setLookupInfo(null);
  };
  const sf = (k, v) => setSendForm(p => ({ ...p, [k]: v }));

  const lookupJob = async () => {
    const ref = sendForm.job_ref.trim();
    if (!ref) { toast.error("Enter a job number first (e.g. XP-0012 or SC-0004)"); return; }
    setLookingUp(true);
    try {
      const r = await api.get("/pcb/job-lookup", { params: { ref } });
      if (!r.data.found) { setLookupInfo({ found: false }); toast.error(`No job found for "${ref}"`); return; }
      const d = r.data;
      setSendForm(p => ({
        ...p, job_ref: d.job_no,
        customer_name: d.customer_name || p.customer_name, customer_phone: d.customer_phone || p.customer_phone,
        customer_address: d.customer_address || p.customer_address, charger_model: d.charger_model || p.charger_model,
        charger_serial: d.charger_serial || p.charger_serial, charger_photo_url: p.charger_photo_url || d.charger_photo_url || "",
      }));
      setLookupInfo({ found: true, source: d.source, job_no: d.job_no });
      toast.success(`Customer details filled from ${d.source} job ${d.job_no}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Lookup failed"); }
    finally { setLookingUp(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error(`Photo too large — max 20 MB (yours: ${(file.size / 1024 / 1024).toFixed(1)} MB)`); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      sf("charger_photo_url", r.data.url); toast.success("Charger photo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Photo upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const sendErrors = {
    customer_name: !sendForm.customer_name.trim(),
    customer_phone: !sendForm.customer_phone.trim(),
    tracking_id: !sendForm.tracking_id.trim(),
    dispatch_date: !isValidDDMMYYYY(sendForm.dispatch_date),
    charger_photo_url: !sendForm.charger_photo_url,
  };
  const canSend = !Object.values(sendErrors).some(Boolean) && !uploading;

  const submitSend = async () => {
    if (!canSend || !sendPcb) return;
    setSending(true);
    try {
      const r = await api.post("/pcb/dispatch", { ...sendForm, pcb_model_id: sendPcb.id });
      toast.success(`${r.data.dispatch_no} created — 1 unit deducted from ${sendPcb.pcb_code}`, {
        action: { label: "Open", onClick: () => navigate(`/inventory/pcb/dispatch/${r.data.id}`) },
      });
      setSendPcb(null); refreshAll();
      navigate(`/inventory/pcb/dispatch/${r.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create dispatch"); }
    finally { setSending(false); }
  };

  const copyLink = (no) => { navigator.clipboard.writeText(pcbTrackUrl(no)).then(() => toast.success("Tracking link copied")); };

  // ─── Quick stats config ───────────────────────
  const quick = stats ? [
    { label: "Total PCB Models",     value: stats.total_models,          icon: CircuitBoard, cls: "text-green-700 bg-green-50" },
    { label: "Total PCB Stock",      value: stats.total_stock,           icon: Layers,       cls: "text-slate-700 bg-slate-100" },
    { label: "Dispatched This Month",value: stats.dispatched_this_month, icon: Send,         cls: "text-blue-700 bg-blue-50" },
    { label: "Old Chargers Received",value: stats.old_chargers_received, icon: Undo2,        cls: "text-amber-700 bg-amber-50" },
    { label: "Under Repair",         value: stats.under_repair,          icon: Wrench,       cls: "text-orange-700 bg-orange-50" },
    { label: "Refurbished & Ready",  value: stats.refurbished_ready,     icon: BadgeCheck,   cls: "text-emerald-700 bg-emerald-50" },
  ] : [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6" data-testid="pcb-inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-0.5">Inventory</p>
          <h1 className="text-2xl font-semibold text-slate-900" style={FONT}>PCB Replacement Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Blank PCB stock, customer dispatches and old-charger returns — all in one flow</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={() => setTab(tab === "inventory" ? "dispatches" : "inventory")} data-testid="pcb-toggle-tab-btn">
            {tab === "inventory" ? <><ClipboardList className="w-4 h-4 mr-2" />Dispatch Records</> : <><Package className="w-4 h-4 mr-2" />PCB Inventory</>}
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowAdd(true)} data-testid="pcb-add-stock-btn">
            <Plus className="w-4 h-4 mr-2" />Add Stock
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {[{ id: "inventory", label: "PCB Inventory", icon: CircuitBoard }, { id: "dispatches", label: "Dispatch Records", icon: ClipboardList }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`pcb-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === t.id ? "border-green-600 text-green-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon size={15} />{t.label}
            {t.id === "dispatches" && stats?.in_progress > 0 && <span className="ml-1 text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-1.5">{stats.in_progress}</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-5">
        {/* ═══════════ LEFT: TABLE ═══════════ */}
        <div className="min-w-0">
          {tab === "inventory" ? (
            <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
                <h2 className="text-sm font-semibold text-slate-900 flex-1" style={FONT}>PCB Inventory <span className="text-slate-400 font-normal">(Internal View)</span></h2>
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search by Model / PCB Code…" value={search} onChange={e => setSearch(e.target.value)}
                    className="pl-9 pr-8 h-9 text-sm bg-white" data-testid="pcb-search-input" />
                  {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="pcb-search-clear"><X size={14} /></button>}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="pcb-inventory-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      {["S.No", "Model", "PCB Code", "Voltage / Current", "Connector Type", "PCB Stock", "Status", "Actions"].map(h => (
                        <th key={h} className={`text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 py-3 whitespace-nowrap ${h === "Actions" || h === "PCB Stock" ? "text-center" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? [...Array(5)].map((_, i) => (
                      <tr key={i}>{[...Array(8)].map((_, j) => <td key={j} className="px-3 py-3"><Skeleton className="h-4" /></td>)}</tr>
                    )) : models.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <CircuitBoard className="w-10 h-10 text-slate-300" />
                          <p className="text-sm font-medium text-slate-700">{search ? `No PCB matches "${search}"` : "No PCB models yet"}</p>
                          {!search && <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowAdd(true)}><Plus size={13} className="mr-1.5" />Add Stock</Button>}
                        </div>
                      </td></tr>
                    ) : models.map((m, i) => (
                      <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors" data-testid={`pcb-row-${m.pcb_code}`}>
                        <td className="px-3 py-2.5 text-sm text-slate-500">{i + 1}</td>
                        <td className="px-3 py-2.5"><span className="text-sm font-semibold text-slate-900">{m.model}</span></td>
                        <td className="px-3 py-2.5"><span className="font-mono text-sm font-bold text-green-700">{m.pcb_code}</span></td>
                        <td className="px-3 py-2.5"><span className="font-mono text-xs text-slate-700">{m.voltage_current || "—"}</span></td>
                        <td className="px-3 py-2.5"><span className="text-sm text-slate-600">{m.connector_type || "—"}</span></td>
                        <td className="px-3 py-2.5 text-center"><span className="text-base font-bold text-slate-900" style={FONT} data-testid={`pcb-stock-${m.pcb_code}`}>{m.stock}</span></td>
                        <td className="px-3 py-2.5">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${STOCK_STATUS_STYLES[m.status]}`} data-testid={`pcb-status-${m.pcb_code}`}>{m.status}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1.5">
                            <Button variant="outline" size="sm" className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => openView(m.id)} data-testid={`pcb-view-${m.pcb_code}`}>
                              <Eye size={12} className="mr-1" />View
                            </Button>
                            <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={m.stock <= 0} onClick={() => openSend(m)} data-testid={`pcb-send-${m.pcb_code}`}>
                              <Send size={12} className="mr-1" />Send
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!loading && models.length > 0 && (
                <div className="px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500">{models.length} PCB model{models.length !== 1 ? "s" : ""}{search ? ` matching "${search}"` : ""}</div>
              )}
            </Card>
          ) : (
            /* ═══════════ DISPATCH RECORDS ═══════════ */
            <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
                <h2 className="text-sm font-semibold text-slate-900 flex-1" style={FONT}>Dispatch Records</h2>
                <Select value={dFilter} onValueChange={setDFilter}>
                  <SelectTrigger className="w-full sm:w-44 h-9 text-sm bg-white" data-testid="pcb-dispatch-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Records</SelectItem>
                    <SelectItem value="active">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    {PCB_STAGES.map(s => <SelectItem key={s.key} value={s.key}>At: {s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search customer, PCB, tracking…" value={dSearch} onChange={e => setDSearch(e.target.value)} className="pl-9 h-9 text-sm bg-white" data-testid="pcb-dispatch-search" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="pcb-dispatch-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      {["Ref", "Customer", "PCB", "Job", "Tracking ID", "Progress", "Current Stage", ""].map(h => (
                        <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingD ? [...Array(4)].map((_, i) => (
                      <tr key={i}>{[...Array(8)].map((_, j) => <td key={j} className="px-3 py-3"><Skeleton className="h-4" /></td>)}</tr>
                    )) : dispatches.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <Send className="w-10 h-10 text-slate-300" />
                          <p className="text-sm font-medium text-slate-700">No dispatch records{dSearch ? ` for "${dSearch}"` : ""}</p>
                          <p className="text-xs text-slate-400">Click <strong>Send</strong> on a PCB in the inventory to create one.</p>
                        </div>
                      </td></tr>
                    ) : dispatches.map(d => (
                      <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors cursor-pointer" onClick={() => navigate(`/inventory/pcb/dispatch/${d.id}`)} data-testid={`pcb-dispatch-row-${d.dispatch_no}`}>
                        <td className="px-3 py-2.5"><span className="font-mono text-sm font-bold text-green-700">{d.dispatch_no}</span><p className="text-[11px] text-slate-400">{prettyDate(d.dispatch_date)}</p></td>
                        <td className="px-3 py-2.5"><p className="text-sm font-medium text-slate-900">{d.customer_name}</p><p className="text-xs text-slate-400">{d.customer_phone}</p></td>
                        <td className="px-3 py-2.5"><p className="font-mono text-xs font-semibold text-slate-800">{d.pcb_code}</p><p className="text-[11px] text-slate-400">{d.pcb_model}</p></td>
                        <td className="px-3 py-2.5">{d.job_ref ? <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">{d.job_ref}</span> : <span className="text-xs text-slate-300">—</span>}</td>
                        <td className="px-3 py-2.5"><span className="font-mono text-xs text-slate-700">{d.tracking_id}</span>{d.courier_name && <p className="text-[11px] text-slate-400">{d.courier_name}</p>}</td>
                        <td className="px-3 py-2.5"><StageDots idx={d.current_stage_index} /></td>
                        <td className="px-3 py-2.5">
                          {d.current_stage_index >= 0 && <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${PCB_STAGES[d.current_stage_index].soft}`}>{d.current_stage_label}</span>}
                        </td>
                        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600" title="Copy tracking link" onClick={() => copyLink(d.dispatch_no)} data-testid={`pcb-copy-link-${d.dispatch_no}`}><Link2 size={13} /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600" title="Open" onClick={() => navigate(`/inventory/pcb/dispatch/${d.id}`)}><ArrowRight size={13} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        {/* ═══════════ RIGHT: QUICK STATS ═══════════ */}
        <aside>
          <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden lg:sticky lg:top-4" data-testid="pcb-quick-stats">
            <div className="px-4 py-3 border-b border-slate-100"><h2 className="text-sm font-semibold text-slate-900" style={FONT}>Quick Stats</h2></div>
            <div className="divide-y divide-slate-100">
              {!stats ? [...Array(6)].map((_, i) => <div key={i} className="px-4 py-3 flex items-center gap-3"><Skeleton className="h-9 w-9 rounded-lg" /><div className="flex-1 space-y-1.5"><Skeleton className="h-3 w-28" /><Skeleton className="h-5 w-10" /></div></div>)
                : quick.map(q => (
                  <div key={q.label} className="px-4 py-3 flex items-center gap-3" data-testid={`pcb-stat-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${q.cls}`}><q.icon size={17} /></div>
                    <div><p className="text-xs text-slate-500">{q.label}</p><p className="text-xl font-bold text-slate-900 leading-tight" style={FONT}>{q.value ?? 0}</p></div>
                  </div>
                ))}
            </div>
            {stats && (stats.low_stock_models > 0 || stats.out_of_stock_models > 0) && (
              <div className="px-4 py-3 border-t border-amber-100 bg-amber-50/60 flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">{stats.out_of_stock_models > 0 && <><strong>{stats.out_of_stock_models}</strong> model{stats.out_of_stock_models !== 1 ? "s" : ""} out of stock. </>}{stats.low_stock_models > 0 && <><strong>{stats.low_stock_models}</strong> low on stock.</>}</p>
              </div>
            )}
          </Card>

          {/* Flow legend */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden mt-4">
            <div className="px-4 py-3 border-b border-slate-100"><h2 className="text-sm font-semibold text-slate-900" style={FONT}>Replacement Flow</h2></div>
            <ol className="px-4 py-3 space-y-2">
              {PCB_STAGES.map((s, i) => (
                <li key={s.key} className="flex items-center gap-2.5">
                  <span className={`h-6 w-6 rounded-full ${s.bg} text-white flex items-center justify-center flex-shrink-0`}><s.icon size={12} /></span>
                  <span className="text-xs text-slate-700"><span className="text-slate-400 mr-1">{i + 1}.</span>{s.label}</span>
                </li>
              ))}
            </ol>
          </Card>
        </aside>
      </div>

      {/* ═══ ADD STOCK DIALOG ═══ */}
      <Dialog open={showAdd} onOpenChange={o => { setShowAdd(o); if (!o) setAddForm(EMPTY_MODEL); }}>
        <DialogContent className="max-w-md" data-testid="pcb-add-dialog">
          <DialogHeader>
            <DialogTitle style={FONT} className="flex items-center gap-2"><span className="h-8 w-8 rounded-lg bg-green-600 text-white flex items-center justify-center"><CircuitBoard size={15} /></span>Add PCB Stock</DialogTitle>
            <DialogDescription>Register a new blank PCB model and its opening stock.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-700">Model <span className="text-rose-500">*</span></Label>
                <Input className="mt-1.5 h-9 text-sm" placeholder="e.g. XP-TW48-10A" value={addForm.model} onChange={e => setAddForm({ ...addForm, model: e.target.value })} data-testid="pcb-add-model" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">PCB Code <span className="text-rose-500">*</span></Label>
                <Input className="mt-1.5 h-9 text-sm font-mono" placeholder="e.g. PCB-48-10A" value={addForm.pcb_code} onChange={e => setAddForm({ ...addForm, pcb_code: e.target.value })} data-testid="pcb-add-code" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-700">Voltage / Current</Label>
                <Input className="mt-1.5 h-9 text-sm font-mono" placeholder="e.g. 54.6V / 10A" value={addForm.voltage_current} onChange={e => setAddForm({ ...addForm, voltage_current: e.target.value })} data-testid="pcb-add-voltage" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Connector Type</Label>
                <Select value={addForm.connector_type} onValueChange={v => setAddForm({ ...addForm, connector_type: v })}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm" data-testid="pcb-add-connector"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CONNECTOR_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-700">PCB Stock (units) <span className="text-rose-500">*</span></Label>
                <Input type="number" min="0" className="mt-1.5 h-9 text-sm font-semibold" placeholder="0" value={addForm.stock} onChange={e => setAddForm({ ...addForm, stock: e.target.value })} data-testid="pcb-add-stock" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Low Stock Alert at</Label>
                <Input type="number" min="0" className="mt-1.5 h-9 text-sm" value={addForm.low_stock_threshold} onChange={e => setAddForm({ ...addForm, low_stock_threshold: e.target.value })} data-testid="pcb-add-threshold" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Notes</Label>
              <Textarea rows={2} className="mt-1.5 text-sm resize-none" placeholder="Optional" value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={!canAdd || saving} onClick={submitAdd} data-testid="pcb-add-submit">
              {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Plus size={13} className="mr-1.5" />}Add PCB Model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ VIEW SHEET ═══ */}
      <Sheet open={!!viewId} onOpenChange={o => { if (!o) { setViewId(null); setEditForm(null); } }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="pcb-view-sheet">
          {loadingV || !viewData ? (
            <div className="space-y-3 pt-6"><Skeleton className="h-6 w-40" /><Skeleton className="h-24" /><Skeleton className="h-40" /></div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle style={FONT} className="flex items-center gap-2"><CircuitBoard size={18} className="text-green-600" />{viewData.model}</SheetTitle>
                <SheetDescription><span className="font-mono font-semibold text-green-700">{viewData.pcb_code}</span> · {viewData.voltage_current || "—"} · {viewData.connector_type || "—"}</SheetDescription>
              </SheetHeader>

              {/* Stock card */}
              <div className="mt-5 p-4 rounded-xl border border-slate-200 bg-slate-50/60">
                <div className="flex items-center justify-between">
                  <div><p className="text-xs text-slate-500">Current Stock</p><p className="text-3xl font-bold text-slate-900" style={FONT} data-testid="pcb-view-stock">{viewData.stock}</p></div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${STOCK_STATUS_STYLES[viewData.status]}`}>{viewData.status}</span>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Input type="number" min="1" className="h-8 w-20 text-sm" value={adjQty} onChange={e => setAdjQty(e.target.value)} data-testid="pcb-adjust-qty" />
                  <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" disabled={adjusting} onClick={() => adjustStock(1)} data-testid="pcb-adjust-add"><Plus size={12} className="mr-1" />Add</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs border-rose-200 text-rose-600 hover:bg-rose-50" disabled={adjusting} onClick={() => adjustStock(-1)} data-testid="pcb-adjust-remove"><Minus size={12} className="mr-1" />Remove</Button>
                  <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white ml-auto" disabled={viewData.stock <= 0} onClick={() => { const m = viewData; setViewId(null); openSend(m); }} data-testid="pcb-view-send"><Send size={12} className="mr-1" />Send</Button>
                </div>
              </div>

              {/* Edit details */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-900" style={FONT}>Details</h3>
                  {!editForm ? <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditForm({ model: viewData.model, pcb_code: viewData.pcb_code, voltage_current: viewData.voltage_current, connector_type: viewData.connector_type, low_stock_threshold: String(viewData.low_stock_threshold ?? 10), notes: viewData.notes || "" })} data-testid="pcb-edit-btn">Edit</Button>
                    : <div className="flex gap-1"><Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditForm(null)}>Cancel</Button><Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" disabled={savingEdit} onClick={saveEdit} data-testid="pcb-edit-save">{savingEdit && <Loader2 size={11} className="mr-1 animate-spin" />}Save</Button></div>}
                </div>
                {editForm ? (
                  <div className="grid grid-cols-2 gap-2.5">
                    <div><Label className="text-xs">Model</Label><Input className="mt-1 h-8 text-sm" value={editForm.model} onChange={e => setEditForm({ ...editForm, model: e.target.value })} /></div>
                    <div><Label className="text-xs">PCB Code</Label><Input className="mt-1 h-8 text-sm font-mono" value={editForm.pcb_code} onChange={e => setEditForm({ ...editForm, pcb_code: e.target.value })} /></div>
                    <div><Label className="text-xs">Voltage / Current</Label><Input className="mt-1 h-8 text-sm font-mono" value={editForm.voltage_current} onChange={e => setEditForm({ ...editForm, voltage_current: e.target.value })} /></div>
                    <div><Label className="text-xs">Connector Type</Label>
                      <Select value={editForm.connector_type} onValueChange={v => setEditForm({ ...editForm, connector_type: v })}>
                        <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CONNECTOR_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label className="text-xs">Low Stock Alert at</Label><Input type="number" min="0" className="mt-1 h-8 text-sm" value={editForm.low_stock_threshold} onChange={e => setEditForm({ ...editForm, low_stock_threshold: e.target.value })} /></div>
                    <div className="col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} className="mt-1 text-sm resize-none" value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} /></div>
                  </div>
                ) : (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div><dt className="text-xs text-slate-400">Low Stock Alert</dt><dd className="text-slate-800">≤ {viewData.low_stock_threshold ?? 10} units</dd></div>
                    <div><dt className="text-xs text-slate-400">Added</dt><dd className="text-slate-800">{fmtDT(viewData.created_at)}</dd></div>
                    {viewData.notes && <div className="col-span-2"><dt className="text-xs text-slate-400">Notes</dt><dd className="text-slate-700">{viewData.notes}</dd></div>}
                  </dl>
                )}
              </div>

              {/* Dispatches */}
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5" style={FONT}><Send size={13} className="text-blue-600" />Dispatches ({viewData.dispatches?.length || 0})</h3>
                {!viewData.dispatches?.length ? <p className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg">No dispatches for this PCB yet</p> : (
                  <div className="space-y-1.5">
                    {viewData.dispatches.map(d => (
                      <button key={d.id} onClick={() => { setViewId(null); navigate(`/inventory/pcb/dispatch/${d.id}`); }} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-green-300 hover:bg-green-50/40 transition-colors flex items-center gap-3">
                        <div className="flex-1 min-w-0"><p className="text-xs font-mono font-bold text-green-700">{d.dispatch_no} <span className="text-slate-400 font-sans font-normal">· {prettyDate(d.dispatch_date)}</span></p><p className="text-sm text-slate-800 truncate">{d.customer_name}</p></div>
                        <StageDots idx={d.current_stage_index} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Stock history */}
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5" style={FONT}><History size={13} className="text-slate-500" />Stock History</h3>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                  {[...(viewData.stock_history || [])].reverse().map(h => (
                    <div key={h.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-50">
                      <span className={`font-mono font-bold w-10 ${h.change > 0 ? "text-green-700" : "text-rose-600"}`}>{h.change > 0 ? "+" : ""}{h.change}</span>
                      <span className="flex-1 text-slate-700 truncate">{h.reason}</span>
                      <span className="text-slate-400 whitespace-nowrap">{fmtDT(h.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" size="sm" className="h-8 text-xs border-rose-200 text-rose-600 hover:bg-rose-50" onClick={() => setShowDel(true)} data-testid="pcb-delete-btn"><Trash2 size={12} className="mr-1.5" />Delete PCB Model</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={showDel} onOpenChange={setShowDel}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {viewData?.pcb_code}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the PCB model and its stock history. Models with linked dispatch records cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" disabled={deleting} onClick={deleteModel} data-testid="pcb-delete-confirm">{deleting ? "Deleting…" : "Delete"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ═══ SEND DIALOG ═══ */}
      <Dialog open={!!sendPcb} onOpenChange={o => { if (!o && !sending) setSendPcb(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pcb-send-dialog">
          <DialogHeader>
            <DialogTitle style={FONT} className="flex items-center gap-2"><span className="h-8 w-8 rounded-lg bg-blue-600 text-white flex items-center justify-center"><Send size={15} /></span>Dispatch PCB to Customer</DialogTitle>
            <DialogDescription>Creates a dispatch record, deducts <strong>1 unit</strong> from stock and generates a shareable tracking link.</DialogDescription>
          </DialogHeader>

          {sendPcb && (
            <div className="space-y-4 py-1">
              {/* PCB summary */}
              <div className="p-3 rounded-xl border border-green-200 bg-green-50 flex items-center gap-3">
                <CircuitBoard size={20} className="text-green-700" />
                <div className="flex-1 text-sm"><p className="font-semibold text-slate-900">{sendPcb.model} <span className="font-mono text-green-700">· {sendPcb.pcb_code}</span></p><p className="text-xs text-slate-600">{sendPcb.voltage_current || "—"} · {sendPcb.connector_type || "—"}</p></div>
                <div className="text-right"><p className="text-[11px] text-slate-500">Stock after send</p><p className="text-lg font-bold text-slate-900" style={FONT}>{sendPcb.stock} → {sendPcb.stock - 1}</p></div>
              </div>

              {/* Job link */}
              <div>
                <Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><ClipboardList size={11} />Link to Customer Job (XP / SC number)</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input className="h-9 text-sm font-mono" placeholder="e.g. XP-0012 or SC-0004" value={sendForm.job_ref} onChange={e => sf("job_ref", e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); lookupJob(); } }} data-testid="pcb-send-job-ref" />
                  <Button type="button" variant="outline" className="h-9 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 whitespace-nowrap" disabled={lookingUp} onClick={lookupJob} data-testid="pcb-send-lookup">{lookingUp ? <Loader2 size={13} className="animate-spin" /> : <><Search size={13} className="mr-1" />Auto-fill</>}</Button>
                </div>
                {lookupInfo?.found && <p className="text-xs text-green-700 mt-1 flex items-center gap-1"><CheckCircle2 size={11} />Filled from {lookupInfo.source} job {lookupInfo.job_no}</p>}
                {lookupInfo && !lookupInfo.found && <p className="text-xs text-amber-700 mt-1">Job not found — you can still enter details manually.</p>}
              </div>

              {/* Customer */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><User size={11} />Customer Name <span className="text-rose-500">*</span></Label>
                  <Input className="mt-1.5 h-9 text-sm" value={sendForm.customer_name} onChange={e => sf("customer_name", e.target.value)} data-testid="pcb-send-customer-name" /></div>
                <div><Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><Phone size={11} />Phone <span className="text-rose-500">*</span></Label>
                  <Input className="mt-1.5 h-9 text-sm" value={sendForm.customer_phone} onChange={e => sf("customer_phone", e.target.value)} data-testid="pcb-send-customer-phone" /></div>
                <div className="sm:col-span-2"><Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><MapPin size={11} />Delivery Address</Label>
                  <Input className="mt-1.5 h-9 text-sm" value={sendForm.customer_address} onChange={e => sf("customer_address", e.target.value)} data-testid="pcb-send-address" /></div>
              </div>

              {/* Charger entry + mandatory photo */}
              <div className="p-3 rounded-xl border border-slate-200 bg-slate-50/50">
                <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1"><Wrench size={11} />Customer's Charger (faulty unit)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs text-slate-600">Charger Model / Spec</Label><Input className="mt-1 h-9 text-sm" placeholder="e.g. 54.6V / 10A" value={sendForm.charger_model} onChange={e => sf("charger_model", e.target.value)} data-testid="pcb-send-charger-model" /></div>
                  <div><Label className="text-xs text-slate-600">Charger Serial / Barcode</Label><Input className="mt-1 h-9 text-sm font-mono" value={sendForm.charger_serial} onChange={e => sf("charger_serial", e.target.value)} data-testid="pcb-send-charger-serial" /></div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-slate-600 flex items-center gap-1"><Camera size={11} />Charger Photo <span className="text-rose-500">* mandatory</span></Label>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => uploadPhoto(e.target.files?.[0])} data-testid="pcb-send-photo-input" />
                  {sendForm.charger_photo_url ? (
                    <div className="mt-1.5 flex items-center gap-3 p-2 rounded-lg border border-green-200 bg-green-50">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${sendForm.charger_photo_url}`} alt="Charger" className="h-16 w-16 rounded-md object-cover border border-green-200" />
                      <div className="flex-1 text-xs text-green-800 flex items-center gap-1"><CheckCircle2 size={13} />Photo uploaded</div>
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="pcb-send-photo-replace">{uploading ? <Loader2 size={12} className="animate-spin" /> : "Replace"}</Button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pcb-send-photo-btn"
                      className="mt-1.5 w-full h-24 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 hover:bg-amber-50 flex flex-col items-center justify-center gap-1 text-amber-800 transition-colors">
                      {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                      <span className="text-xs font-medium">{uploading ? "Uploading…" : "Upload charger photo (required, max 20 MB)"}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Courier */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs font-medium text-slate-700">Courier Name</Label><Input className="mt-1.5 h-9 text-sm" placeholder="e.g. Delhivery" value={sendForm.courier_name} onChange={e => sf("courier_name", e.target.value)} data-testid="pcb-send-courier" /></div>
                <div><Label className="text-xs font-medium text-slate-700">Tracking ID <span className="text-rose-500">*</span></Label><Input className="mt-1.5 h-9 text-sm font-mono" placeholder="AWB / Tracking no." value={sendForm.tracking_id} onChange={e => sf("tracking_id", e.target.value)} data-testid="pcb-send-tracking" /></div>
                <div><Label className="text-xs font-medium text-slate-700">Dispatch Date <span className="text-rose-500">*</span></Label>
                  <Input className={`mt-1.5 h-9 text-sm font-mono ${sendForm.dispatch_date && sendErrors.dispatch_date ? "border-rose-300 focus-visible:ring-rose-300" : ""}`} placeholder="DD/MM/YYYY" inputMode="numeric" value={sendForm.dispatch_date} onChange={e => sf("dispatch_date", maskDDMMYYYY(e.target.value))} data-testid="pcb-send-date" />
                  {sendForm.dispatch_date && sendErrors.dispatch_date && <p className="text-[11px] text-rose-600 mt-0.5">Use DD/MM/YYYY</p>}</div>
              </div>
              <div><Label className="text-xs font-medium text-slate-700">Notes</Label><Textarea rows={2} className="mt-1.5 text-sm resize-none" placeholder="Optional internal note" value={sendForm.notes} onChange={e => sf("notes", e.target.value)} /></div>

              {!sendForm.charger_photo_url && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="pcb-send-photo-warning"><ImageIcon size={13} />A charger photo must be uploaded before this record can be saved.</p>}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSendPcb(null)} disabled={sending}>Cancel</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={!canSend || sending} onClick={submitSend} data-testid="pcb-send-submit">
              {sending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Send size={13} className="mr-1.5" />}Dispatch & Deduct 1 Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
